/**
 * A connection's week of live use, as the line under its row says it (spec P-3.5): "Last used
 * 2 hours ago · 41 worked, 3 failed this week", a failing flag, and why it fails in the owner's
 * words.
 *
 * The BFF decides the STATE (`idle`, `failing`, `ok`: three failures in the week, the last call
 * among them). Nothing here re-derives it: the Overview row reads the same field, so the two
 * can never disagree about which connection is "not answering".
 *
 * NOTHING IS DERIVED FROM AN ABSENCE. `health: null` is an older BFF, or a week the sidecar
 * could not read: no line at all, never "not used this week".
 */
import { relativeTime } from './relative-time.js';
import { toIntlLocale } from './intl.js';

const key = name => `WASLO.SETTINGS.CONNECTIONS.HEALTH.${name}`;

/** The sidecar's error kinds (`tools/http/outcome.ts`), less `http`, which reads the status. */
const KIND_KEYS = {
  timeout: 'REASON.TIMEOUT',
  network: 'REASON.NETWORK',
  blocked: 'REASON.BLOCKED',
  invalid_input: 'REASON.INVALID_INPUT',
  missing_contact_detail: 'REASON.MISSING_CONTACT_DETAIL',
  not_given: 'REASON.NOT_GIVEN',
  switched_off: 'REASON.SWITCHED_OFF',
};

/** Which sentence says why the last call failed, or `null` when it did not fail. */
export const healthReasonKey = health => {
  if (!health || health.lastOk !== false) return null;
  if (health.lastErrorKind === 'http') {
    const status = health.lastStatus;
    if (status === 401 || status === 403) return 'REASON.SIGN_IN';
    if (status === 404) return 'REASON.NOT_FOUND';
    if (typeof status === 'number' && status >= 500) return 'REASON.SERVER';
    return 'REASON.HTTP';
  }
  return Object.hasOwn(KIND_KEYS, health.lastErrorKind)
    ? KIND_KEYS[health.lastErrorKind]
    : 'REASON.NETWORK';
};

/**
 * @param {{ health?: ?object }} connection A row from `wasloApi.getConnections`.
 * @param {(key: string, params?: object, n?: number) => string} t `useWasloI18n().t`.
 * @param {string} locale vue-i18n's locale.
 * @param {number} [now]
 * @returns {{ line: string, failing: boolean, reason: string }}
 */
export const connectionHealth = (connection, t, locale, now = Date.now()) => {
  const health = connection?.health ?? null;
  if (!health) return { line: '', failing: false, reason: '' };
  if (health.state === 'idle') {
    return { line: t(key('IDLE')), failing: false, reason: '' };
  }
  const digits = new Intl.NumberFormat(toIntlLocale(locale));
  const week = t(key('WEEK'), {
    worked: t(
      key('WORKED'),
      { count: digits.format(health.worked) },
      health.worked
    ),
    failed: t(
      key('FAILED'),
      { count: digits.format(health.failed) },
      health.failed
    ),
  });
  const when = relativeTime(health.lastAt, locale, {
    now,
    justNow: t(key('JUST_NOW')),
  });
  const failing = health.state === 'failing';
  const reasonKey = failing ? healthReasonKey(health) : null;
  return {
    line: when ? `${t(key('LAST_USED'), { when })} · ${week}` : week,
    failing,
    reason: reasonKey
      ? t(key(reasonKey), { status: health.lastStatus ?? '' })
      : '',
  };
};

export default connectionHealth;
